import { useParams, Link } from 'react-router-dom';
import SchemaOrg from '@/components/feature/SchemaOrg';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import DramaCard from '@/pages/home/components/DramaCard';
import { categories } from '@/mocks/categories';
import { getDramasByCategory } from '@/mocks/dramas';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const dramas = slug ? getDramasByCategory(slug) : [];

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
          </div>
          <h2 className="text-xl font-heading font-semibold text-foreground-100 mb-2">分类未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">该分类不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SchemaOrg type="WebPage" data={{
        name: `${category.name} - 星辰影院`,
        description: category.description,
        url: `https://xingchencinema.com/category/${slug}`,
      }} />
      <SEOHead
        title={`${category.name} | 星辰影院 - ${category.description}`}
        description={`星辰影院${category.name}频道：${category.description}。汇集最新${category.name}资源，高清在线观看不缓冲。`}
        keywords={`星辰影院,${category.name},免费电视剧,电视剧全集,高清在线观看,不缓冲`}
        canonicalUrl={`/category/${slug}`}
      />

      <main className="min-h-screen pt-20 pb-12">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="mb-6">
            <Breadcrumb items={getCategoryBreadcrumb(slug)} />
          </div>

          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/10">
                <i className={`${category.icon} text-xl text-primary-400`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">{category.name}</h1>
            </div>
            <p className="text-sm text-foreground-500">{category.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-100 border border-background-200/60'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {dramas.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {dramas.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-background-100">
                <i className="ri-movie-2-line text-3xl text-foreground-500"></i>
              </div>
              <p className="text-sm text-foreground-500">该分类暂无剧集，敬请期待</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}